import axios from "axios";
import {ENDPOINT_BASE_URL} from "../../configs/config";

// 创建一个数据缓存和共享机制
class DataCache {
    constructor() {
        this.cache = {};
        this.pendingPromises = {};
        this.expiryTimes = {};
        this.DEFAULT_EXPIRY = 1000; // 默认1s缓存
    }

    // 获取或创建请求
    async getOrFetch(key, fetchFn, expiryTime = this.DEFAULT_EXPIRY) {
        const now = Date.now();

        // 如果缓存有效且未过期
        if (this.cache[key] && this.expiryTimes[key] > now) {
            return this.cache[key];
        }

        // 如果相同请求正在进行中，等待该请求完成
        if (this.pendingPromises[key]) {
            return this.pendingPromises[key];
        }

        // 创建新请求
        this.pendingPromises[key] = fetchFn().then(result => {
            // 缓存结果
            this.cache[key] = result;
            this.expiryTimes[key] = now + expiryTime;
            // 完成后清除挂起的promise
            delete this.pendingPromises[key];
            return result;
        }).catch(error => {
            // 错误时也要清除挂起的promise
            delete this.pendingPromises[key];
            throw error;
        });

        return this.pendingPromises[key];
    }

    // 清除特定缓存
    clearCache(key) {
        if (key) {
            delete this.cache[key];
            delete this.expiryTimes[key];
        } else {
            this.cache = {};
            this.expiryTimes = {};
        }
    }
}

// 创建单例
const dataCache = new DataCache();

export const fetchadjustDailyPortfolioData = async (startDate, endDate) => {
    const cacheKey = `portfolio_daily_${startDate}_${endDate}`;

    return dataCache.getOrFetch(cacheKey, async () => {
        try {
            const response = await axios.get(`${ENDPOINT_BASE_URL}/track_records_daily_latest`, {
                params: {
                    start_date: startDate,
                    end_date: endDate
                }
            });

            let dailyDataArray = [];
            let thirty_day_apy = [];
            let ninety_day_apy = [];
            let correspondingDay = [];
            let dollar_value_growth = [];

            for (let i = 0; i < response.data.data.length - 20; i++) {
                const record = response.data.data[i];
                dailyDataArray.push(record.portfolio_implied_apr);
                thirty_day_apy.push(record.thirty_day_apy);
                ninety_day_apy.push(record.ninety_day_apy);
                correspondingDay.push(record.timestamp);
                dollar_value_growth.push(record.dollar_value_growth);
            }

            dollar_value_growth.push(1);
            correspondingDay.push("Tue, 22 Jul 2024 23:11:49 GMT");

            return {
                datasets1: dailyDataArray,
                datasets2: thirty_day_apy,
                datasets3: ninety_day_apy,
                datasets4: dollar_value_growth,
                labels: correspondingDay
            };
        } catch (error) {
            console.error('Error fetching portfolio track records:', error);
            throw error;
        }
    });
};

// 同样优化fetchPortfolioTrackRecordsRoundFourDigit
export const fetchPortfolioTrackRecordsRoundFourDigit = async () => {
    const cacheKey = 'portfolio_records_latest';

    return dataCache.getOrFetch(cacheKey, async () => {
        try {
            const response = await axios.get(`${ENDPOINT_BASE_URL}/portfolio_track_records`, {
                params: {
                    page: 1,
                    limit: 5
                }
            });

            return reformatPortfolioTrackRecordRoundFourDigit(response.data.data[0]);
        } catch (error) {
            console.error('Error fetching portfolio track records:', error);
            throw error;
        }
    });
};

// 提供手动刷新缓存的方法
export const refreshPortfolioData = (startDate = null, endDate = null) => {
    if (startDate && endDate) {
        dataCache.clearCache(`portfolio_daily_${startDate}_${endDate}`);
    } else {
        // 清除所有缓存
        dataCache.clearCache();
    }
};

export const reformatPortfolioTrackRecordRoundFourDigit = (data) => {
    // Helper function to safely parse and convert to percentage, handling null/undefined
    const parseApy = (value) => {
        if (value === null || value === undefined) return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : (parsed * 100).toFixed(4) * 1;
    };

    return {
        totalValue: parseFloat(data.total_assets_value).toFixed(4) * 1,
        impliedApr: (parseFloat(data.portfolio_implied_apr) * 100).toFixed(4) * 1,
        thirtyDayEarning: parseFloat(data.portfolio_implied_30days_earnings).toFixed(4) * 1,
        moneyAtRisk: parseFloat(data.money_at_risk_percent).toFixed(4) * 1,
        nev: parseFloat(data.net_exposure_value).toFixed(4) * 1,
        thirtyDayApy: parseApy(data.thirty_day_apy),
        ninetyDayApy: parseApy(data.ninety_day_apy),
        usdiInCirculation: ((parseFloat(data.usdi_in_circulation)).toFixed(2) * 1),
        dollar_value_growth: ((parseFloat(data.dollar_value_growth)).toFixed(4) * 1)
    };
};